import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronRight, RefreshCw, Upload } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Chip,
  EmptyState,
  QueryErrorState,
  groupRows,
  KanbanBoard,
  PageHeaderBand,
  SearchInput,
  Select,
  Toolbar,
  ToolbarCount,
  ToolbarDivider,
  ToolbarLeft,
  ToolbarRight,
  ViewToggle,
  toast,
  type GroupAccent,
  type TableGroup,
} from '@/components/ui'
import { useHasAnyPermission } from '@/shell/module-system'
import { CofaBatchUploader } from '@/shell/services/cofa-uploader'
import {
  CofaKpiStrip,
  CofaPreviewDrawer,
  DEFAULT_FILTERS,
  DocumentGrid,
  DocumentList,
  DocumentTable,
  groupDocuments,
  PRODUCT_TYPE_LABEL,
  PRODUCT_TYPE_ORDER,
  useCofaDocuments,
  useCofaStats,
  type CofaClientFilters,
  type CofaKindFilter,
  type DocumentRow,
  type GroupBy,
  type IdentityFilter,
  type LookupQuery,
  type StatusFilter,
} from '@/shell/services/cofa-registry'
import { useCofaPipeline } from '../api/queries'
import { useApprovePipelineItem, useRejectPipelineItem, useRetryPipelineItem } from '../api/queries'
import type { PipelineCounts, PipelineRow, Stage } from '../api/types'
import { PipelineCard } from '../components/PipelineCard'
import { PipelineDrawer } from '../components/PipelineDrawer'
import {
  STAGES,
  STAGE_ACCENT,
  STAGE_LABELS,
  type MutationAction,
  transitionFor,
} from '../components/stage-meta'

// Document-status group labels/accents for the grouped table view.
const STATUS_GROUP_LABEL: Record<string, string> = {
  complete: 'Complete',
  missing_spi: 'Missing SPI',
  missing_vendor: 'Missing vendor',
}
const STATUS_GROUP_ACCENT: Record<string, GroupAccent> = {
  complete: 'success',
  missing_spi: 'warning',
  missing_vendor: 'danger',
}

/**
 * CofA Docs — management dashboard for admin + regulatory + operations.
 *
 * Sprint 04b rewrite (2026-04-20): previously a pure kanban page at
 * `/cofa`. Now the full management surface at `/cofa-docs`:
 *
 *   - Page header: title + KPI strip + Upload CofAs primary button,
 *     right-aligned so the action sits where the user's eye already is
 *     when the completion/missing-SPI numbers demand it.
 *   - SPI CofA pipeline summary strip directly below the header and
 *     above the toolbar/search — it's the work queue the Upload CTA
 *     feeds, so it belongs next to the CTA, not stranded at the bottom
 *     of the page (Chris, 2026-04-23). Click-to-expand surfaces the
 *     full kanban for stage-level drag-to-advance; default collapsed
 *     because "kanban hogs too much screen real estate" (Chris,
 *     2026-04-20).
 *   - Primary list surface: vendor-grouped lot registry (from
 *     cofa-registry) with per-row admin actions (Rename / Replace /
 *     Remove) and inline Upload-on-missing. Backed by the shared
 *     CofaPreviewDrawer with admin affordances enabled.
 *
 * Read-only library for all users (sales, warehouse, etc.) lives at
 * `/cofa`, rendered by the `cofa-documents` module.
 */
export function CofaPipelinePage() {
  const qc = useQueryClient()
  // ['cofa-documents'] prefix-matches the stats + lookup keys too.
  const refreshCofaLists = () => {
    void qc.invalidateQueries({ queryKey: ['cofa-documents'] })
    void qc.invalidateQueries({ queryKey: ['cofa-pipeline'] })
  }
  // Two role groups: ops staff manage vendor CofAs (rename/remove/
  // replace), but only admin + regulatory approve/reject SPI CofA
  // pipeline rows. Backend mirrors this split via COFA_DOC_ROLES vs
  // COFA_ADMIN_ROLES in procurement_cofa.py.
  const canMutate = useHasAnyPermission(['admin', 'regulatory', 'operations', 'purchasing'])
  const canApprove = useHasAnyPermission(['admin', 'regulatory'])

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [filters, setFilters] = useState<CofaClientFilters>(DEFAULT_FILTERS)
  const [viewMode, setViewMode] = useState<'table' | 'list' | 'grid'>('table')
  const [groupBy, setGroupBy] = useState<GroupBy>('vendor')
  const [selectedDoc, setSelectedDoc] = useState<{
    lotId: number
    tab: 'vendor' | 'spi'
  } | null>(null)
  const [uploaderOpen, setUploaderOpen] = useState(false)
  const [pipelineExpanded, setPipelineExpanded] = useState(false)
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 200)
    return () => clearTimeout(id)
  }, [search])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && e.target instanceof HTMLElement) {
        const typing =
          e.target.tagName === 'INPUT' ||
          e.target.tagName === 'TEXTAREA' ||
          e.target.isContentEditable
        if (typing) return
        e.preventDefault()
        searchRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Registry data (primary surface)
  const documentsQuery = useMemo<LookupQuery>(
    () => ({
      q: debouncedSearch,
      status: filters.status as StatusFilter,
      identity: filters.identity,
      limit: 500,
      offset: 0,
      scope: filters.includeUsedUp ? 'all' : 'in_stock',
    }),
    [debouncedSearch, filters.status, filters.identity, filters.includeUsedUp],
  )
  const {
    data: docsData,
    isLoading: docsLoading,
    isFetching: docsFetching,
    isError: docsError,
    error: docsErrorValue,
    refetch: refetchDocs,
  } = useCofaDocuments(documentsQuery)
  const { data: stats } = useCofaStats()

  const docRows = useMemo<DocumentRow[]>(() => docsData?.lots ?? [], [docsData])

  const filteredDocs = useMemo<DocumentRow[]>(() => {
    const vendorNeedle = filters.vendorQuery.trim().toLowerCase()
    const productNeedle = filters.productQuery.trim().toLowerCase()
    return docRows.filter((row) => {
      if (filters.spiOnly && !row.is_spi_product) return false
      if (filters.excludeBothMissing && !row.vendor_cofa && !row.spi_cofa) return false
      if (filters.cofaKind === 'vendor' && !row.vendor_cofa) return false
      if (filters.cofaKind === 'spi' && !row.spi_cofa) return false
      if (vendorNeedle && !row.vendor_name.toLowerCase().includes(vendorNeedle)) return false
      if (productNeedle && !row.product_name.toLowerCase().includes(productNeedle)) return false
      return true
    })
  }, [docRows, filters])

  const docGroups = useMemo(() => groupDocuments(filteredDocs, groupBy), [filteredDocs, groupBy])

  // Table-view grouping. Status uses the generic groupRows so all three
  // statuses always render (empty ones included with a `(0)` header);
  // vendor/product/date reuse the existing groupDocuments output (no empty
  // buckets needed there). 'none' → flat table.
  const tableGroups = useMemo<TableGroup<DocumentRow>[] | undefined>(() => {
    if (viewMode !== 'table' || groupBy === 'none') return undefined
    if (groupBy === 'status') {
      return groupRows(filteredDocs, {
        keyOf: (r) => r.status,
        groupOrder: ['complete', 'missing_spi', 'missing_vendor'],
        labelOf: (k) => STATUS_GROUP_LABEL[k] ?? k,
        accentOf: (k) => STATUS_GROUP_ACCENT[k],
      })
    }
    if (groupBy === 'product_type') {
      return groupRows(filteredDocs, {
        keyOf: (r) => (r.is_sample ? 'sample' : 'commercial'),
        groupOrder: [...PRODUCT_TYPE_ORDER],
        labelOf: (k) => PRODUCT_TYPE_LABEL[k] ?? k,
      })
    }
    return docGroups as TableGroup<DocumentRow>[]
  }, [viewMode, groupBy, filteredDocs, docGroups])

  const selectedDocRow = useMemo(() => {
    if (!selectedDoc) return null
    return filteredDocs.find((r) => r.lot_id === selectedDoc.lotId) ?? null
  }, [filteredDocs, selectedDoc])

  // Open on the certificate customers receive (SPI CofA for SPI products)
  // unless the user narrowed the page to one source.
  const tabFor = (row: DocumentRow): 'vendor' | 'spi' =>
    filters.cofaKind === 'spi' ? 'spi' : filters.cofaKind === 'vendor' ? 'vendor' : row.is_spi_product ? 'spi' : 'vendor'

  // Pipeline data (secondary — SPI-CofA auto-generation stages)
  const { data: pipelineData, refetch: refetchPipeline } = useCofaPipeline()
  const pipelineItems = useMemo<PipelineRow[]>(() => pipelineData?.items ?? [], [pipelineData])
  const pipelineCounts = useMemo(
    () =>
      pipelineData?.counts ?? {
        all: 0,
        generation_queued: 0,
        generation_running: 0,
        awaiting_approval: 0,
        generation_failed: 0,
        approved: 0,
        rejected: 0,
        cancelled: 0,
      },
    [pipelineData],
  )
  const approvedToday = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10)
    return pipelineItems.filter(
      (r) => r.stage === 'approved' && (r.stage_updated_at?.slice(0, 10) ?? '') === today,
    ).length
  }, [pipelineItems])

  const approve = useApprovePipelineItem()
  const reject = useRejectPipelineItem()
  const retry = useRetryPipelineItem()
  const pendingPipelineIds = useMemo(() => {
    const ids = new Set<number>()
    if (approve.isPending && approve.variables !== undefined) ids.add(approve.variables)
    if (reject.isPending && reject.variables !== undefined) ids.add(reject.variables)
    if (retry.isPending && retry.variables !== undefined) ids.add(retry.variables)
    return ids
  }, [
    approve.isPending,
    approve.variables,
    reject.isPending,
    reject.variables,
    retry.isPending,
    retry.variables,
  ])

  function runPipelineAction(row: PipelineRow, action: MutationAction) {
    const mutation = action === 'approve' ? approve : action === 'reject' ? reject : retry
    mutation.mutate(row.id, {
      onSuccess: () => {
        const verb =
          action === 'approve' ? 'Approved' : action === 'reject' ? 'Rejected' : 'Requeued'
        toast.success(`${verb} ${row.lot_name}`)
      },
      onError: (err) => {
        toast.danger(
          `Could not ${action} ${row.lot_name}${err instanceof Error ? `: ${err.message}` : ''}`,
        )
      },
    })
  }

  const selectedPipelineRow = useMemo(() => {
    if (!selectedPipelineId) return null
    return pipelineItems.find((r) => String(r.id) === selectedPipelineId) ?? null
  }, [pipelineItems, selectedPipelineId])

  const boardItems = useMemo(
    () => pipelineItems.map((r) => ({ ...r, id: String(r.id), stageId: r.stage })),
    [pipelineItems],
  )
  const boardStages = useMemo(
    () =>
      STAGES.map((s) => ({
        id: s,
        label: `${STAGE_LABELS[s]} · ${pipelineCounts[s] ?? 0}`,
        accent: STAGE_ACCENT[s],
      })),
    [pipelineCounts],
  )

  return (
    <div data-density="compact" className="flex h-full flex-col">
      <PageHeaderBand
        title="CofA Management"
        description="Manage vendor and SPI CofAs across every tracked lot. Upload, rename, replace, remove — or drill into the SPI-CofA generation pipeline below."
      >
        <div className="ml-auto flex items-center gap-4">
          <CofaKpiStrip stats={stats} />
          {canMutate ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setUploaderOpen(true)}
              title="Drop vendor CofA PDFs to create or update lots in Odoo"
            >
              <Upload size={14} />
              Upload CofAs
            </Button>
          ) : null}
        </div>
      </PageHeaderBand>

      <div className="flex min-h-0 flex-1 flex-col">
        <PipelineSummaryStrip
          counts={pipelineCounts}
          approvedToday={approvedToday}
          expanded={pipelineExpanded}
          onToggle={() => setPipelineExpanded((v) => !v)}
        />

        {pipelineExpanded ? (
          <div className="max-h-[50vh] overflow-y-auto border-b border-border-subtle bg-canvas px-3 pb-3">
            <KanbanBoard
              stages={boardStages}
              items={boardItems}
              selectedId={selectedPipelineId ?? undefined}
              allowedTransitions={(from, to) => transitionFor(from as Stage, to as Stage) !== null}
              onAdvance={(item, from, to) => {
                const source = pipelineItems.find((r) => r.id === Number(item.id))
                const t = transitionFor(from as Stage, to as Stage)
                if (source && t) runPipelineAction(source, t.action)
              }}
              onSelect={(item) => setSelectedPipelineId(item.id)}
              renderCard={(item) => {
                const row = pipelineItems.find((r) => r.id === Number(item.id))
                if (!row) return null
                return (
                  <PipelineCard
                    row={row}
                    canAct={canApprove}
                    pending={pendingPipelineIds.has(row.id)}
                    onApprove={(r) => runPipelineAction(r, 'approve')}
                    onReject={(r) => runPipelineAction(r, 'reject')}
                    onRetry={(r) => runPipelineAction(r, 'retry')}
                  />
                )
              }}
            />
          </div>
        ) : null}

        <Toolbar>
          <SearchInput
            ref={searchRef}
            containerClassName="flex-1 max-w-none"
            placeholder="Search lot, product, vendor…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search"
          />
        </Toolbar>
        <Toolbar>
          <ToolbarLeft>
            <ViewToggle
              aria-label="View mode"
              value={viewMode}
              onChange={setViewMode}
              options={[
                { value: 'table', label: 'Table' },
                { value: 'list', label: 'List' },
                { value: 'grid', label: 'Grid' },
              ]}
            />
            <ToolbarDivider />
            <Select
              aria-label="Source"
              value={filters.cofaKind}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  cofaKind: e.target.value as CofaKindFilter,
                })
              }
            >
              <option value="all">All sources</option>
              <option value="vendor">Vendor CofA</option>
              <option value="spi">SPI CofA</option>
            </Select>
            <Select
              aria-label="Status"
              value={filters.status}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  status: e.target.value as StatusFilter,
                })
              }
            >
              <option value="all">All statuses</option>
              <option value="complete">Complete</option>
              <option value="missing_spi">Missing SPI</option>
              <option value="missing_vendor">Missing vendor</option>
            </Select>
            <Select
              aria-label="Identity"
              value={filters.identity}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  identity: e.target.value as IdentityFilter,
                })
              }
            >
              <option value="all">All identities</option>
              <option value="needs_attention">Needs attention</option>
              <option value="mismatch">Mismatch</option>
              <option value="unverifiable">Needs verification</option>
              <option value="verified">Verified</option>
              <option value="missing">No CofA</option>
            </Select>
            <label className="flex items-center gap-1.5 text-role-section-label">
              Group
              <Select
                aria-label="Group by"
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value as GroupBy)}
              >
                <option value="status">Status</option>
                <option value="vendor">Vendor</option>
                <option value="product">Product</option>
                <option value="product_type">Commercial / Sample</option>
                <option value="date">Expiry month</option>
                <option value="none">None</option>
              </Select>
            </label>
            <ToolbarDivider />
            <label className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-role-toolbar text-secondary">
              <input
                type="checkbox"
                checked={filters.spiOnly}
                onChange={(e) => setFilters({ ...filters, spiOnly: e.target.checked })}
              />
              SPI only
            </label>
            <label className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-role-toolbar text-secondary">
              <input
                type="checkbox"
                checked={filters.excludeBothMissing}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    excludeBothMissing: e.target.checked,
                  })
                }
              />
              Hide missing both
            </label>
            <label className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-role-toolbar text-secondary">
              <input
                type="checkbox"
                checked={filters.includeUsedUp}
                onChange={(e) => setFilters({ ...filters, includeUsedUp: e.target.checked })}
              />
              Include used-up lots
            </label>
          </ToolbarLeft>
          <ToolbarRight>
            <ToolbarCount>
              {filteredDocs.length.toLocaleString()} of {(docsData?.total ?? docRows.length).toLocaleString()}
            </ToolbarCount>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Refresh"
              loading={docsFetching && !docsLoading}
              onClick={() => {
                void refetchDocs()
                void refetchPipeline()
              }}
            >
              <RefreshCw size={14} />
            </Button>
          </ToolbarRight>
        </Toolbar>

        <div className="flex-1 min-h-0 overflow-auto bg-canvas p-4">
          {docsError ? (
            <QueryErrorState
              error={docsErrorValue}
              onRetry={() => void refetchDocs()}
              subject="CofA lots"
            />
          ) : filteredDocs.length === 0 && !docsLoading ? (
            <EmptyState
              variant="no-match"
              title="No lots match"
              description="Try clearing a filter or broadening the search."
              action={
                <Button variant="secondary" onClick={() => setFilters(DEFAULT_FILTERS)}>
                  Reset filters
                </Button>
              }
            />
          ) : viewMode === 'grid' ? (
            <DocumentGrid
              groups={docGroups}
              selectedId={selectedDoc?.lotId ?? null}
              onSelect={(row) => setSelectedDoc({ lotId: row.lot_id, tab: tabFor(row) })}
            />
          ) : viewMode === 'list' ? (
            <DocumentList
              groups={docGroups}
              selectedId={selectedDoc?.lotId ?? null}
              onSelect={(row) => setSelectedDoc({ lotId: row.lot_id, tab: tabFor(row) })}
              canAct={canMutate}
              onRequestBatchUpload={() => setUploaderOpen(true)}
            />
          ) : (
            <DocumentTable
              loading={docsLoading}
              rows={filteredDocs}
              groups={tableGroups}
              onSelect={(row) => setSelectedDoc({ lotId: row.lot_id, tab: tabFor(row) })}
              onSelectKind={(row, tab) => setSelectedDoc({ lotId: row.lot_id, tab })}
              canAct={canMutate}
              onRequestBatchUpload={() => setUploaderOpen(true)}
              moduleId="cofa-management"
            />
          )}
        </div>
      </div>

      <CofaPreviewDrawer
        row={selectedDocRow}
        initialTab={selectedDoc?.tab ?? 'vendor'}
        canAct={canMutate}
        canVerify={canApprove}
        onClose={() => setSelectedDoc(null)}
      />

      <PipelineDrawer
        row={selectedPipelineRow}
        canAct={canApprove}
        canChangeProduct={canMutate}
        pending={selectedPipelineRow ? pendingPipelineIds.has(selectedPipelineRow.id) : false}
        onClose={() => setSelectedPipelineId(null)}
        onApprove={(r) => runPipelineAction(r, 'approve')}
        onReject={(r) => runPipelineAction(r, 'reject')}
        onRetry={(r) => runPipelineAction(r, 'retry')}
      />

      <CofaBatchUploader
        open={uploaderOpen}
        onOpenChange={setUploaderOpen}
        onClose={refreshCofaLists}
        onApplied={refreshCofaLists}
      />
    </div>
  )
}

/**
 * Compact one-line summary strip of the SPI-CofA generation pipeline.
 * Replaces the full-page kanban that "hogs too much screen real
 * estate." Shows stage counts + Approved today; clicking toggles the
 * full kanban below.
 */
function PipelineSummaryStrip({
  counts,
  approvedToday,
  expanded,
  onToggle,
}: {
  counts: PipelineCounts
  approvedToday: number
  expanded: boolean
  onToggle: () => void
}) {
  const items: Array<{
    key: string
    label: string
    value: number
    variant?: 'warning' | 'danger' | 'success' | 'info'
  }> = [
    {
      key: 'awaiting_approval',
      label: 'Awaiting',
      value: counts.awaiting_approval,
      variant: 'warning',
    },
    {
      key: 'generation_running',
      label: 'Running',
      value: counts.generation_running,
      variant: 'info',
    },
    { key: 'generation_queued', label: 'Queued', value: counts.generation_queued, variant: 'info' },
    {
      key: 'generation_failed',
      label: 'Failed',
      value: counts.generation_failed,
      variant: 'danger',
    },
    { key: 'approved_today', label: 'Approved today', value: approvedToday, variant: 'success' },
  ]
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-label={expanded ? 'Collapse SPI CofA pipeline' : 'Expand SPI CofA pipeline'}
      className="flex cursor-pointer items-center gap-3 border-b border-border-subtle bg-surface-sunk px-4 py-2 text-left hover:bg-surface-hover"
    >
      {expanded ? (
        <ChevronDown size={14} className="flex-shrink-0 text-tertiary" />
      ) : (
        <ChevronRight size={14} className="flex-shrink-0 text-tertiary" />
      )}
      <span className="text-role-section-label">SPI CofA Pipeline</span>
      <div className="flex flex-1 items-center gap-2 overflow-x-auto">
        {items.map((it) => (
          <span key={it.key} className="inline-flex items-center gap-1.5 text-xs text-secondary">
            <span className="text-tertiary">{it.label}</span>
            <Chip size="xs" variant={it.variant ?? 'neutral'}>
              {it.value.toLocaleString()}
            </Chip>
          </span>
        ))}
      </div>
      <span className="text-2xs text-tertiary">{expanded ? 'Hide kanban' : 'Show kanban'}</span>
    </button>
  )
}
